import { logTrace, logError } from './loggerService';
import { showErrorModal } from './errorModalService';
import { showError as showErrorToast } from './toastService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const X_API_KEY = import.meta.env.VITE_X_API_KEY || '';

// State that persists as long as the user is logged in
let jwt: string | null = null;
let activeProfileId: string | null = null;

// Token refresh state
let tokenRefreshHandler: (() => Promise<boolean>) | null = null;
let refreshInProgress: Promise<boolean> | null = null;

// Setters for authentication state
export function setJWT(token: string | null) {
  jwt = token;
}

export function setActiveProfileId(profileId: string | null) {
  activeProfileId = profileId;
}

export function clearAuthState() {
  jwt = null;
  activeProfileId = null;
}

// Register a handler for refreshing expired tokens
export function setTokenRefreshHandler(handler: (() => Promise<boolean>) | null) {
  tokenRefreshHandler = handler;
}

// Attempt to refresh the JWT token, coalescing concurrent attempts
async function attemptTokenRefresh(): Promise<boolean> {
  if (!tokenRefreshHandler) return false;

  // If a refresh is already in progress, wait for it instead of starting another
  if (refreshInProgress) {
    return refreshInProgress;
  }

  refreshInProgress = tokenRefreshHandler().finally(() => {
    refreshInProgress = null;
  });

  return refreshInProgress;
}

// Getters for checking current state
export function getJWT(): string | null {
  return jwt;
}

export function getActiveProfileId(): string | null {
  return activeProfileId;
}

// Build headers for API requests
function buildHeaders(includeContentType = true, serviceKey?: string): HeadersInit {
  const headers: Record<string, string> = {};

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  // If serviceKey is provided, use it instead of JWT
  if (serviceKey) {
    headers['Authorization'] = `Bearer ${serviceKey}`;
  } else if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }

  if (X_API_KEY) {
    headers['X-Api-Key'] = X_API_KEY;
  }

  if (activeProfileId) {
    headers['X-Sub-Identity'] = activeProfileId;
  }

  return headers;
}

// Generic request helper
async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false,
  serviceKey?: string
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const method = options.method || 'GET';

  const response = await fetch(url, {
    ...options,
    headers: {
      ...buildHeaders(true, serviceKey),
      ...options.headers,
    },
  });

  if (!response.ok) {
    // On 401, attempt silent token refresh and retry (skip for login endpoints and retries)
    if (response.status === 401 && !isRetry && !serviceKey && !endpoint.startsWith('/login')) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        return request<T>(endpoint, options, true, serviceKey);
      }
    }

    const errorBody = await response.text();

    // Try to extract correlationId from the error response
    let correlationId: string | undefined;
    try {
      const errorJson = JSON.parse(errorBody);
      correlationId = errorJson.correlationId || errorJson.CorrelationId;
    } catch {
      // Not JSON or no correlationId, continue without it
    }

    const error = new Error(`API Error ${response.status}: ${errorBody}`);
    logError(error, `${method} ${endpoint} failed`, undefined, {
      status: response.status,
      endpoint,
      body: errorBody,
      correlationId,
    });

    if (response.status === 401) {
      // Login endpoints handle their own error UI — no modal or toast needed
      if (!endpoint.startsWith('/login')) {
        showErrorToast('You are no longer authenticated. Redirecting you to Sign In.', [], { duration: 5000 });
      }
    } else if (response.status === 400) {
      // Toast bad request errors with the message
      let msg = 'Bad Request';
      try {
        const errorJson = JSON.parse(errorBody);
        msg = (errorJson.message || 'Bad Request').replace(/\s*\[.*?\]\s*$/, '');
      } catch {}
      showErrorToast(msg, [], { duration: 5000 });
    } else {
      showErrorModal(error.message, correlationId);
    }

    throw error;
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    logTrace(`${method} ${endpoint} completed`, undefined, { status: 204 });
    return undefined as T;
  }

  const data = await response.json();
  logTrace(`${method} ${endpoint} completed`, undefined, { status: response.status, data });
  return data;
}

// HTTP method helpers
export async function get<T>(endpoint: string, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, { method: 'GET' }, false, serviceKey);
}

// GET that returns null on error instead of showing the error modal.
// Useful for lookup endpoints where a missing record is expected.
export async function getSilent<T>(endpoint: string): Promise<T | null> {
  const url = `${API_BASE_URL}${endpoint}`;
  try {
    const response = await fetch(url, { headers: buildHeaders() });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

export async function getById<T>(endpoint: string, id: string, queryParams?: string, serviceKey?: string): Promise<T> {
  const url = queryParams ? `${endpoint}/${id}?${queryParams}` : `${endpoint}/${id}`;
  return request<T>(url, { method: 'GET' }, false, serviceKey);
}

export async function post<T>(endpoint: string, body: unknown, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  }, false, serviceKey);
}

export async function put<T>(endpoint: string, body: unknown, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  }, false, serviceKey);
}

export async function patch<T>(endpoint: string, body: unknown, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }, false, serviceKey);
}

export async function del<T>(endpoint: string, serviceKey?: string): Promise<T> {
  return request<T>(endpoint, { method: 'DELETE' }, false, serviceKey);
}

export async function postFormData<T>(endpoint: string, formData: FormData, isRetry = false): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders(false),
    body: formData,
  });

  if (!response.ok) {
    // On 401, attempt silent token refresh and retry
    if (response.status === 401 && !isRetry) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        return postFormData<T>(endpoint, formData, true);
      }
    }

    const errorBody = await response.text();

    // Try to extract correlationId from the error response
    let correlationId: string | undefined;
    try {
      const errorJson = JSON.parse(errorBody);
      correlationId = errorJson.correlationId || errorJson.CorrelationId;
    } catch {
      // Not JSON or no correlationId, continue without it
    }

    const error = new Error(`API Error ${response.status}: ${errorBody}`);
    logError(error, `POST ${endpoint} failed`, undefined, {
      status: response.status,
      endpoint,
      body: errorBody,
      correlationId,
    });

    if (response.status === 401) {
      showErrorToast('You are no longer authenticated. Redirecting you to Sign In.', [], { duration: 5000 });
    } else {
      showErrorModal(error.message, correlationId);
    }

    throw error;
  }

  if (response.status === 204) {
    logTrace(`POST ${endpoint} completed`, undefined, { status: 204 });
    return undefined as T;
  }

  const data = await response.json();
  logTrace(`POST ${endpoint} completed`, undefined, { status: response.status, data });
  return data;
}

// Convenience export for all methods
export const apiService = {
  get,
  getSilent,
  getById,
  post,
  postFormData,
  put,
  patch,
  delete: del,
  setJWT,
  setActiveProfileId,
  clearAuthState,
  getJWT,
  getActiveProfileId,
  setTokenRefreshHandler,
};
